import { authService } from './authService'

// Base URL for the API Gateway (same pattern as wimService)
import { API_GATEWAY_BASE_URL } from '@/config/api'

// System config service endpoints via gateway
const SYSTEM_CONFIG_SERVICE_BASE = `${API_GATEWAY_BASE_URL}/api/system-config`

/**
 * System config API - all calls go through the gateway at /api/system-config/*.
 * Uses the same pattern as wimService (gateway base + /api/system-config/...).
 */
class SystemConfigService {
  private getApi() {
    return authService.getApi()
  }

  /** Build full asset URL through gateway (for logos, banners, etc.) */
  getAssetUrl(relativePath: string): string {
    if (!relativePath) return ''
    const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`
    const normalized = path.replace(/^\/systemassets/, '')
    return `${SYSTEM_CONFIG_SERVICE_BASE}/SystemAsset${normalized}`
  }

  /** Gateway base URL for building URLs in composables */
  getGatewayBaseUrl(): string {
    return API_GATEWAY_BASE_URL
  }

  getSystemSettingsBase(): string {
    return SYSTEM_CONFIG_SERVICE_BASE
  }

  async getSystemSettings() {
    const response = await this.getApi().get('/system-config/SystemSettings')
    return response.data
  }

  async updateSystemSettings(settings: any) {
    await this.getApi().put('/system-config/SystemSettings', settings)
  }

  async postSystemAsset(endpoint: string, formData: FormData, options?: { headers?: Record<string, string> }) {
    const response = await this.getApi().post(`/system-config/SystemAsset/${endpoint}`, formData, {
      headers: options?.headers ?? { 'Content-Type': 'multipart/form-data' },
      ...options,
    })
    return response
  }

  async getModules() {
    const response = await this.getApi().get('/system-config/Module')
    return response.data ?? []
  }

  async getEnabledModules() {
    const response = await this.getApi().get('/system-config/Module/enabled')
    return response.data ?? []
  }

  async updateModule(id: number, data: { isEnabled?: boolean; isVisible?: boolean; [key: string]: any }) {
    await this.getApi().put(`/system-config/Module/${id}`, data)
  }

  async getCompany() {
    const response = await this.getApi().get('/system-config/Company')
    return response.data ?? []
  }

  async deleteCompany(id: number) {
    await this.getApi().delete(`/system-config/Company/${id}`)
  }

  async getOrganization() {
    const response = await this.getApi().get('/system-config/Organization')
    return response.data ?? []
  }

  async deleteOrganization(id: number) {
    await this.getApi().delete(`/system-config/Organization/${id}`)
  }

  async getStation() {
    const response = await this.getApi().get('/system-config/Station')
    return response.data ?? []
  }

  async deleteStation(id: number) {
    await this.getApi().delete(`/system-config/Station/${id}`)
  }

  async getLanguage() {
    const response = await this.getApi().get('/system-config/Language')
    return response.data ?? []
  }

  async getLicense() {
    const response = await this.getApi().get('/system-config/License')
    return response.data ?? []
  }

  async healthCheck(): Promise<boolean> {
    try {
      const api = this.getApi()
      const response = await api.get('/system-config/health')
      return response.status === 200
    } catch {
      return false
    }
  }
}

export const systemConfigService = new SystemConfigService()
export { API_GATEWAY_BASE_URL, SYSTEM_CONFIG_SERVICE_BASE }
export default systemConfigService
